import type { FormState, FormStateHandler } from '../DataImport';

import { type ReactNode, useCallback, useEffect, useRef } from 'react';

type FormStateWrapperProps = {
  readonly renderForm: (
    updateForm: FormStateHandler,
    context: Record<string, unknown>,
  ) => ReactNode;
  readonly onFormChange: FormStateHandler;
  readonly context: Record<string, unknown>;
};

export function FormStateWrapper({
  renderForm,
  onFormChange,
  context,
}: FormStateWrapperProps) {
  // Use a ref to persist the form state between renders
  const formStateRef = useRef<FormState>({
    data: {},
    errors: {},
    isValid: false,
  });

  // Create a stable callback that updates our ref first, then passes to parent
  const stableUpdateForm = useCallback(
    (formState: FormState) => {
      // Update the ref with the latest form state
      formStateRef.current = formState;
      // Pass the updated state to the parent
      onFormChange(formState);
    },
    [onFormChange],
  );

  // On mount or when onFormChange changes, restore form state if we have any
  useEffect(() => {
    // If we have existing form data, send it to the parent on mount
    if (
      formStateRef.current &&
      Object.keys(formStateRef.current.data).length > 0
    ) {
      onFormChange(formStateRef.current);
    }
  }, [onFormChange]);

  // Render the form with our stable update function
  return renderForm(stableUpdateForm, context);
}
